import { useEffect, useCallback, useState, useRef } from "react";
import { UploadedFile } from "../types/UploadedFile.type";
import { useFileUploadContext } from "./FileUploadContext";

interface UseFileUploadItemParams {
	file: UploadedFile;
	extraData?: Record<string, any>;
	onComplete: (file: UploadedFile, serverData: any) => void;
	onError?: () => void;
	onStart?: () => void;
}

/**
 * Hook to upload individual files using shared context
 * @param param0
 * @returns
 */
export function useFileUploadItem({
	file,
	extraData,
	onComplete,
	onError,
	onStart,
}: UseFileUploadItemParams) {
	const { uploadFile, getUploadState, cancelUpload, clearUploadState } =
		useFileUploadContext();

	// Get the state for this specific upload
	const uploadState = getUploadState(file.uploadId);
	const { progress, isUploading, error, isUploadCompleted } = uploadState;

	// Track if we've already started this upload
	const hasEverStartedRef = useRef(false);

	const startUpload = useCallback(() => {
		if (file.raw && !hasEverStartedRef.current) {
			hasEverStartedRef.current = true;
			onStart?.();
			uploadFile(file, extraData, (serverFile) => {
				onComplete(file, serverFile);
			});
		}
	}, [file, uploadFile, extraData, onStart, onComplete]);

	// Auto-start upload for new files
	useEffect(() => {
		if (file.raw && !hasEverStartedRef.current) {
			startUpload();
		}
	}, [file.raw, startUpload]);

	// Handle errors
	useEffect(() => {
		if (error && onError) {
			onError();
		}
	}, [error, onError]);

	// Clean up state when component unmounts (file removed from list)
	useEffect(() => {
		return () => {
			// Only clean up if upload is complete or hasn't started
			// Don't clean up if actively uploading
			if (isUploadCompleted || !hasEverStartedRef.current) {
				clearUploadState(file.uploadId);
			}
		};
	}, [file.uploadId, isUploadCompleted, clearUploadState]);

	const retryUpload = useCallback(() => {
		if (file.raw) {
			hasEverStartedRef.current = false;
			// Trigger startUpload on next render
			setTimeout(() => startUpload(), 0);
		}
	}, [file.raw, startUpload]);

	return {
		progress,
		isUploading,
		error,
		cancelUpload,
		retryUpload,
	};
}
